import React, { useEffect, useRef, useState } from 'react';
import { checkValue } from '../../helper';
import { ClickStreamEventConstants, ClickStreamEventType } from 'src/utils/event-wrapper';

interface VideoProps {
  src: string;
  srcSet?: any,
  sizes?: string,
  exClass?: string;
  muted?: boolean;
  overrideMuted?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  controls?: boolean;
  poster?: string;
  playsInline?: boolean;
  heroScrollDownArrow?: boolean;
  hideSoundControlsForVideo?: boolean;
  isAutoPlayWhenInViewport?: boolean;
  hideVideoPlayControls?: string;
  themeTokens?: any;
  customControlComponnet?: React.ComponentType<customControlProps>;
  onTimeUpdate?: (e) => void;
  playBtnColor?: string;
  trackClickVideo?: (arg: any) => void;
  eventCategory?: string;
  eventLocation?: string;
  componentName?: string;
  altText?: string;
}

export type customControlProps = {
  isPlaying: boolean;
  hideSoundControlsForVideo: boolean;
  currentTime: string;
  progress: number;
  isMuted: boolean;
  withSounds?: boolean;
  togglePlayWithSound?: () => void;
  togglePlaysound: () => void;
  togglePlay: () => void;
  themeTokens?: any;
  playBtnColor?: string;
};

export type CustomControlComponentType = React.ComponentType<customControlProps>;

const Video: React.FC<VideoProps> = (props) => {
  const {
    src = '',
    poster = 'https://mesocp3.azureedge.net/media/fj5lu14p/aaa.png',
    srcSet = {},
    sizes = '', 
    exClass = '',
    autoPlay = false,
    heroScrollDownArrow,
    hideSoundControlsForVideo,
    isAutoPlayWhenInViewport,
    hideVideoPlayControls = 'False',
    customControlComponnet: CustomControlComponent = null,
    themeTokens,
    onTimeUpdate,
    playBtnColor,
    overrideMuted,
    trackClickVideo,
    eventCategory = 'breaker_video',
    eventLocation = '',
    componentName = '',
    altText = 'video',
    ...rest
  } = props;

  if (!checkValue(src)) {
    return null;
  }

  const videoElement = useRef(null);
  const minutesToHoursAndMinutes = (n) => Math.floor(n / 60) + '.' + (n % 60);

  const [playerState, setPlayerState] = useState({
    isPlaying: autoPlay,
    progress: 0,
    currentTime: '0.0',
    speed: 1,
    isMuted: overrideMuted ? rest.muted : true,
  });

  const trackClickstream = () => {   
    if (trackClickVideo) {
      const videoDuration = videoElement.current.duration || 0;
      const videoTitle = src.split('/').pop() || 'Unknown Title';
      const clickstreamDataImage = {
        EVENT_NAME: playerState.isPlaying ? 'video_pause.selected' : 'video_play.selected', // Use the previous state
        EVENT_TYPE: ClickStreamEventType.INTERACTION,
        EVENT_CATEGORY: eventCategory,
        EVENT_LOCATION: eventLocation,
        VIDEO_LIST: {
          //video_title: videoTitle,
          video_id: videoTitle,
          video_url: src,
          video_duration: Math.round(videoDuration) || 0,
          video_type: 'HTML5',
        },
      };
      const clickstreamDataImageString = JSON.stringify(clickstreamDataImage);
      trackClickVideo(clickstreamDataImageString);
    }
  }

  const togglePlay = () => {
    setPlayerState({
      ...playerState,
      isPlaying: !playerState.isPlaying,
    });
    trackClickstream();
  };

  const togglePlaysound = () => {
    setPlayerState({
      ...playerState,
      isMuted: !playerState.isMuted,
    });
    trackClickstream();
  };

  const togglePlayWithSound = () => {
    setPlayerState({
      ...playerState,
      isMuted: !playerState.isMuted,
      isPlaying: !playerState.isPlaying,
    });
    trackClickstream();
  };

  useEffect(() => {
    playerState.isPlaying ? videoElement.current.play() : videoElement.current.pause();
  }, [playerState.isPlaying, videoElement]);

  useEffect(() => {
    playerState.isMuted ? (videoElement.current.muted = true) : (videoElement.current.muted = false);
    videoElement.current.volume = 1;
  }, [playerState.isMuted, videoElement]);

  const handleOnTimeUpdate = () => {
    const progress = Math.round((videoElement.current.currentTime / videoElement.current.duration) * 100);
    const currentTime = minutesToHoursAndMinutes(videoElement.current.currentTime.toFixed(0));
    setPlayerState({
      ...playerState,
      progress,
      currentTime,
    });
    onTimeUpdate && onTimeUpdate({ currentTime: currentTime, duration: videoElement.current.duration, progress });
  };

  const handleScroll = () => {
    let scrollToModule: HTMLElement;
    const heroBanner = document.getElementById('hero') || document.querySelector('.global-hero-module-container');
    const headerOffsetHeight = document.querySelector<HTMLElement>('.header-main-container').offsetHeight;
    const sweeptakemodule = document.getElementById('sweepstakes');
    const entryform = document.querySelector('.competition-entry');
    if (checkValue(sweeptakemodule)) {
      scrollToModule = sweeptakemodule;
    } else if (checkValue(entryform)) {
      scrollToModule = entryform as HTMLElement;
    } else {
      scrollToModule = heroBanner.parentElement.nextSibling as HTMLElement;
    }

    if (checkValue(heroBanner)) {
      setTimeout(() => {
        window.scrollTo({
          behavior: 'smooth',
          top:
            // heroBanner.parentElement.nextElementSibling.getBoundingClientRect().top -
            // scrollToModule.parentElement.getBoundingClientRect().top -
            scrollToModule.getBoundingClientRect().top - document.body.getBoundingClientRect().top - headerOffsetHeight,
        });
      }, 400);
    }
  };

  //video play and pause when in viewport
  useEffect(() => {
    if (!isAutoPlayWhenInViewport) {
      return; // Do nothing if autoplay is not enabled
    }
    const options = {
      root: null, // Use the viewport as the root
      rootMargin: '0px',
      threshold: 0.5, // Trigger when 50% of the video is in the viewport
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          videoElement.current.play();
          setPlayerState({
            ...playerState,
            isPlaying: true,
          });
        } else {
          videoElement.current.pause();
          setPlayerState({
            ...playerState,
            isPlaying: false,
          });
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, options);

    // Start observing the video element
    if (videoElement.current) {
      observer.observe(videoElement.current);
    }

    // Cleanup observer when component unmounts
    return () => {
      if (videoElement.current) {
        observer.unobserve(videoElement.current);
      }
    };
  }, [isAutoPlayWhenInViewport]);

  return (
    <>
      <video
        aria-label={altText}
        className={exClass}
        ref={videoElement}
        {...rest}
        src={src}
        poster={poster}
        {...(srcSet ? { srcSet } : {})}
        {...(sizes ? { sizes } : {})}
        onTimeUpdate={handleOnTimeUpdate}
        muted
      />
      {checkValue(CustomControlComponent) && (
        <CustomControlComponent
          {...playerState}
          themeTokens={themeTokens}
          togglePlay={togglePlay}
          hideSoundControlsForVideo={hideSoundControlsForVideo}
          togglePlaysound={togglePlaysound}
          togglePlayWithSound={togglePlayWithSound}
          playBtnColor={playBtnColor}
        />
      )}
      {!checkValue(CustomControlComponent) && (
        <div className="controls container">
          {!heroScrollDownArrow && (
            <span className="controls__arrow" onClick={handleScroll}>
              <span></span>
              <span></span>
            </span>
          )}

          {hideVideoPlayControls !== 'True' && (
            <div className="actions">
              <button onClick={togglePlay} className={`${playerState.isPlaying ? 'paused' : ''}`}></button>
              <span>{playerState.currentTime}</span>
              <div className="actions__progress">
                <div style={{ width: `${playerState.progress}%` }}></div>
              </div>
              {!hideSoundControlsForVideo && (
                <div onClick={togglePlaysound} className={`speaker ${playerState.isMuted ? 'on' : ''}`}>
                  <div id="mute" className="mute"></div>
                  <span></span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export { VideoProps, Video };
